import type { FC, ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { cx } from "shared/utils";
import type {
  OwnerScreeningReport,
  ReportApplicationFacts
} from "api/owner-screening-report";
import { APPLICATION_FACT_ROWS } from "../constants";
import { formatDate, formatField } from "../format";
import { Figure } from "./Figure";
import { MetricCard } from "./MetricCard";
import { PersonCard } from "./PersonCard";

const applicationFigures = (facts: ReportApplicationFacts) =>
  APPLICATION_FACT_ROWS.map((row) => ({
    label: row.label,
    value: formatField(facts[row.key], row.format)
  })).filter((figure): figure is { label: string; value: string } =>
    Boolean(figure.value)
  );

/**
 * Report name, then the place, on one line — skipping whatever is absent.
 *
 * Two separators on purpose, as the design writes it: a dot divides the report
 * from the property, while the unit is part of the address and takes a comma.
 * "Screening summary · 1420 Maple Grove Ave, Unit 4B"
 */
const titleFor = (
  reportTitle: string,
  property: { address?: string; unit?: string }
) => {
  const place = [property.address, property.unit].filter(Boolean).join(", ");

  return [reportTitle, place].filter(Boolean).join(" · ");
};

/**
 * Who shared it, from where, when — and the date the results were frozen.
 *
 * Every part is optional and drops out on its own: a preview has no share date
 * because nothing has been shared, and a link from before the sender was
 * recorded has no name. Whatever is left still reads as a sentence.
 */
const attributionFor = ({
  sharedBy,
  partnerName,
  sharedOn,
  resultsAsOf
}: {
  sharedBy?: string | null;
  partnerName?: string | null;
  sharedOn?: string | null;
  resultsAsOf: string | null;
}) => {
  const shared = sharedBy
    ? [
        `Shared by ${sharedBy}`,
        partnerName ? `from ${partnerName}` : null,
        sharedOn ? `on ${sharedOn}` : null
      ]
        .filter(Boolean)
        .join(" ")
    : null;

  return [shared, resultsAsOf ? `Results as of ${resultsAsOf}` : null]
    .filter(Boolean)
    .join(" · ");
};

/**
 * The report itself, with no idea where it came from. The owner's page renders a
 * link's frozen payload through this; the share dialog's Preview renders one
 * built on the spot. Sharing the component is what keeps the two honest about
 * each other.
 */
export const ReportBody: FC<{
  report: OwnerScreeningReport;
  /** Rendered opposite the attribution — the owner's page puts Download PDF here. */
  actions?: ReactNode;
  partnerName?: string | null;
  logoUrl?: string | null;
  /** First name of the partner user who shared it, or would share it. */
  sharedBy?: string | null;
  /** When it was shared. A preview has none: nothing has been shared yet. */
  sharedOn?: string | null;
  /** The sender's note to the owner. Absent when they wrote none. */
  message?: string | null;
  /** Mobile widths — the preview's Mobile tab, and small screens on the real page. */
  compact?: boolean;
}> = ({
  report,
  actions,
  partnerName,
  logoUrl,
  sharedBy,
  sharedOn,
  message,
  compact
}) => {
  const facts = applicationFigures(report.summary.application);
  const resultsAsOf = formatDate(report.results_as_of);
  const gutter = compact ? "px-5" : "px-6";
  const hasBrand = Boolean(logoUrl) || Boolean(partnerName);
  const attribution = attributionFor({
    sharedBy,
    partnerName,
    sharedOn,
    resultsAsOf
  });

  return (
    // Page grey behind white cards, so the cards read as cards. Deliberately
    // background-800 rather than the usual page background-900: 900 is #faf8f9,
    // five parts off white, which against a white card is no separation at all.
    // Printing goes back to white, as Layout does.
    <div className="flex flex-col bg-background-800 print:bg-white">
      {/* The branded bar: who the report is from in its own cell, a rule, then
          what the report is.

          Deliberately outside the reading column below — it spans the whole
          surface, so its rule runs edge to edge like the preview's toolbar
          above it and the width of the page the owner prints. */}
      <header>
        <div
          className={cx(
            "flex border-b border-alpha-40 bg-background-1000",
            // Two cells side by side, as the design has it. Stacked only on a
            // narrow frame, where the brand would leave the title a couple of
            // words wide.
            compact ? "flex-col" : "items-stretch"
          )}
        >
          <Show if={hasBrand}>
            <div
              className={cx(
                "flex shrink-0 items-center gap-2.5",
                gutter,
                compact ? "pt-4" : "py-4"
              )}
            >
              <Show if={Boolean(logoUrl)}>
                <img
                  src={logoUrl ?? ""}
                  alt={partnerName ?? ""}
                  className="h-7 w-auto object-contain object-left"
                />
              </Show>
              <Show if={Boolean(partnerName)}>
                <span className="text-sm font-bold text-content-1000">
                  {partnerName}
                </span>
              </Show>
            </div>
          </Show>

          <div
            className={cx(
              "flex min-w-0 flex-1 items-center",
              gutter,
              compact ? "pb-4 pt-2" : "py-4",
              hasBrand && !compact && "border-l border-alpha-40"
            )}
          >
            <h1
              className={cx(
                "min-w-0 font-bold text-content-1000",
                compact ? "text-base" : "text-lg"
              )}
            >
              {titleFor(report.report_title, report.property)}
            </h1>
          </div>
        </div>
      </header>

      {/* The reading column. Everything but the bar above is held to a
          comfortable measure and centred, however wide the surface is. */}
      <div className="mx-auto w-full max-w-[960px]">
        <Show if={Boolean(attribution) || Boolean(actions)}>
          <div
            className={cx(
              "flex flex-wrap items-center justify-between gap-3 pt-6",
              gutter
            )}
          >
            <p className="text-sm text-content-400">{attribution}</p>
            {actions}
          </div>
        </Show>

        <div className={cx("flex flex-col gap-6 pb-10 pt-6", gutter)}>
          {/* What the sender wrote to the owner, in their words and above the
              figures — it is the reason the owner opened the link. */}
          <Show if={Boolean(message)}>
            <section className="break-inside-avoid rounded-xl border border-alpha-100 bg-background-1000 p-5 shadow-xs">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-20">
                  <HugeiconsIcon
                    icon={BubbleChatIcon}
                    size={18}
                    className="text-primary-500"
                  />
                </span>
                <h2 className="text-base font-bold text-content-1000">
                  {sharedBy ? `Message from ${sharedBy}` : "Message"}
                </h2>
              </div>
              {/* Preserves the sender's own line breaks rather than running
                  their note into one paragraph. */}
              <p className="mt-3 whitespace-pre-line text-sm text-content-700">
                {message}
              </p>
            </section>
          </Show>

          <Show if={facts.length > 0 || report.summary.metrics.length > 0}>
            <section className="break-inside-avoid overflow-hidden rounded-xl border border-alpha-100 bg-background-1000 shadow-xs">
              {/* Heading and scope on one line, divided from the figures —
                  the padding sits inside each band so the rule runs the full
                  width of the card rather than stopping at a gutter. */}
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-alpha-40 px-6 py-4">
                <h2 className="text-lg font-bold text-content-1000">
                  Application summary
                </h2>
                <p className="text-sm text-content-400">
                  {report.summary.scope_label}
                </p>
              </div>

              <div className="p-6">
                <Show if={facts.length > 0}>
                  <div
                    className={cx(
                      "grid gap-x-6 gap-y-4",
                      compact ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"
                    )}
                  >
                    {facts.map((figure) => (
                      <Figure
                        key={figure.label}
                        label={figure.label}
                        value={figure.value}
                      />
                    ))}
                  </div>
                </Show>

                <Show if={report.summary.metrics.length > 0}>
                  <div
                    className={cx(
                      "grid gap-4",
                      facts.length > 0 && "mt-6",
                      compact
                        ? "grid-cols-1"
                        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                    )}
                  >
                    {report.summary.metrics.map((metric) => (
                      <MetricCard key={metric.metric} metric={metric} />
                    ))}
                  </div>
                </Show>
              </div>
            </section>
          </Show>

          {/* No "People" heading: the design runs the person cards straight on
              from the summary, and each card is already named. */}
          <Show if={report.people.length > 0}>
            <div className="flex flex-col gap-4">
              {/* Name and role are not unique: the report can show two occupants
                  as "Jordan A.", so position is what keeps the keys apart. */}
              {report.people.map((person, index) => (
                <PersonCard
                  key={`${index}-${person.name}-${person.role_label}`}
                  person={person}
                  compact={compact}
                />
              ))}
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};
